// Boats on water (RED-6, M16.2 water transport). PURE (ENG-1): no three/DOM/audio.
// A boat is a free-floating entity (no rails): buoyancy settles it to the water
// surface, paddling adds forward thrust along its yaw, and water drag coasts it to
// a stop. A chest boat additionally carries an inventory. The host adapts the world
// (water + solidity), binds a rider's steering input, and renders from (x,y,z,yaw).
import { BOAT } from '../config';

export type BoatKind = 'boat' | 'chest_boat';

/** A stored stack in a chest boat (opaque to the physics). */
export interface BoatSlot { item: number; count: number; }

export interface Boat {
  readonly id: number;
  readonly kind: BoatKind;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  /** Heading in radians (0 = −z, matching the player yaw convention). */
  yaw: number;
  /** True while the hull is floating on water this tick. */
  onWater: boolean;
  /** Container payload (chest boat) — the host reads/writes slots. */
  items?: (BoatSlot | null)[];
}

/** The paddle/steer controls a rider feeds the boat. */
export interface BoatInput {
  forward?: boolean; back?: boolean; left?: boolean; right?: boolean;
}

/** What the boat physics reads from the world. */
export interface BoatWorld {
  isWater(x: number, y: number, z: number): boolean;
  isSolid(x: number, y: number, z: number): boolean;
}

/** Slots in a chest boat (Java: 27 like a single chest). */
export const CHEST_BOAT_SIZE = 27;

/** Top Y (world) of the water column supporting a boat whose origin is near `y`,
 *  or null if there is no water at/just under it. Scans a couple of blocks down so
 *  a boat dropped from a small height still finds the surface. */
export function waterSurfaceUnder(world: BoatWorld, x: number, y: number, z: number): number | null {
  const fx = Math.floor(x), fz = Math.floor(z);
  const top = Math.floor(y + 0.2);
  for (let cy = top; cy >= top - 2; cy--) {
    if (world.isWater(fx, cy, fz)) return cy + 1; // the surface is this cell's top face
  }
  return null;
}

export class BoatSystem {
  readonly boats: Boat[] = [];
  private nextId = 1;

  get count(): number { return this.boats.length; }

  spawn(x: number, y: number, z: number, yaw = 0, kind: BoatKind = 'boat'): Boat {
    const boat: Boat = { id: this.nextId++, kind, x, y, z, vx: 0, vy: 0, vz: 0, yaw, onWater: false };
    if (kind === 'chest_boat') boat.items = new Array<BoatSlot | null>(CHEST_BOAT_SIZE).fill(null);
    this.boats.push(boat);
    return boat;
  }

  remove(boat: Boat): void {
    const i = this.boats.indexOf(boat);
    if (i >= 0) this.boats.splice(i, 1);
  }

  clear(): void { this.boats.length = 0; }

  /** Advance every boat one tick. `inputFor` supplies a ridden boat's steering
   *  (an empty input for an unmanned boat, which just drifts + floats). */
  tick(world: BoatWorld, inputFor: (b: Boat) => BoatInput = () => ({})): void {
    for (const boat of this.boats) stepBoat(boat, inputFor(boat), world);
  }
}

function stepBoat(boat: Boat, input: BoatInput, world: BoatWorld): void {
  // Steering: turn the hull, then paddle forward/back along the new heading.
  if (input.left) boat.yaw -= BOAT.TURN;
  if (input.right) boat.yaw += BOAT.TURN;
  const fwd = (input.forward ? 1 : 0) - (input.back ? 1 : 0);
  if (fwd !== 0) {
    boat.vx += -Math.sin(boat.yaw) * BOAT.ACCEL * fwd;
    boat.vz += -Math.cos(boat.yaw) * BOAT.ACCEL * fwd;
  }

  // Water drag coasts the boat to a stop.
  boat.vx *= BOAT.H_DRAG;
  boat.vz *= BOAT.H_DRAG;
  if (Math.hypot(boat.vx, boat.vz) < BOAT.STOP_EPS) { boat.vx = 0; boat.vz = 0; }

  // Buoyancy vs. gravity: a boat on water eases toward its floating line; off
  // water it falls.
  const surface = waterSurfaceUnder(world, boat.x, boat.y, boat.z);
  if (surface !== null) {
    boat.onWater = true;
    boat.vy += (surface - BOAT.DRAFT - boat.y) * BOAT.BUOY;
    boat.vy *= BOAT.V_DRAG;
  } else {
    boat.onWater = false;
    boat.vy -= BOAT.GRAVITY;
  }

  moveAxis(boat, world, 'x', boat.vx);
  moveAxis(boat, world, 'z', boat.vz);
  moveAxis(boat, world, 'y', boat.vy);
}

/** Move the boat along one axis, blocked by solids (blunt half-width collision). */
function moveAxis(boat: Boat, world: BoatWorld, axis: 'x' | 'y' | 'z', vel: number): void {
  if (vel === 0) return;
  const next = boat[axis] + vel;
  const h = BOAT.HALF;
  const probe = next + Math.sign(vel) * (axis === 'y' ? (vel > 0 ? 1 : 0) : h);
  const px = axis === 'x' ? probe : boat.x;
  const py = axis === 'y' ? probe : boat.y;
  const pz = axis === 'z' ? probe : boat.z;
  if (world.isSolid(Math.floor(px), Math.floor(py), Math.floor(pz))) {
    boat[axis === 'x' ? 'vx' : axis === 'y' ? 'vy' : 'vz'] = 0; // stop at the obstacle
    return;
  }
  boat[axis] = next;
}
